import type { PreflightResult } from '@ctcells/contract'
import { batchStatusLabel, t, type BatchStatus } from '@ctcells/i18n'

/**
 * **「왜 시작이 안 되나」 — 프론트가 아는 사실로 짠 대조표.** 순수 함수라 단독으로 시험한다.
 *
 * 서버 `allowed_commands()`(`ctcells-core app/services/batches.py`)가 `start` 를 싣는 조건은
 * 다섯의 AND 다: ① 쓰기 역할 ② 제어락 보유 ③ 상태 `created|queued` ④ 다른 배치가
 * `running|paused|error` 아님 ⑤ Pre-flight `passed`. 응답에는 **결과만** 오고 사유 칸이 없다.
 *
 * 🔴 **추론이 아니라 대조다.** 각 줄은 프론트가 따로 받은 값(세션 권한 · 락 보관소 · 활성 목록 ·
 *    `POST /batches/{id}/preflight` 응답)과 1:1 이다. 서버가 `start` 를 실었으면 **빈 배열** —
 *    화면 쪽 사실이 뭐라 하든 서버가 이긴다(버튼 활성은 여전히 `allowed_commands` 그대로다).
 * 🔴 **다 통과인데 `start` 가 없으면 「사유 미제공」이라고 말한다.** 없는 사유를 지어내지 않는다.
 * ⚠ 이 목록은 **활성 근거가 아니다.** 비활성 «사유 문구»만 만든다.
 */

/** 서버 `BATCH_FROM_START` — 이 상태에서만 `start` 가 뜻을 가진다 */
const FROM_START: readonly BatchStatus[] = ['created', 'queued']

/**
 * 서버 `BLOCKING_STATUSES`(`services/batches.py`) — **다른** 배치가 이 상태면 시작이 막힌다.
 * ⚠ 서버 표를 옮겨 적은 것이다. 서버가 바꾸면 여기도 같이 바꾼다.
 */
export const START_BLOCKING_STATUSES: readonly BatchStatus[] = ['running', 'paused', 'error']

export interface StartFacts {
  /** 이 배치의 서버 상태 */
  status: BatchStatus
  /** 서버가 준 집합 그대로 */
  allowedCommands: readonly string[]
  /** 구독 stale — 참이면 아래 사실이 전부 낡았다 */
  streamStale: boolean
  /** 세션 권한에 `control` 이 있는가(서버 `WRITE_ROLES`) */
  canControl: boolean
  /** 이 세션이 제어락을 쥐고 있는가(리스 보관소) */
  lockHeld: boolean
  /** 활성 목록의 **다른** 배치들(자기 자신 제외) */
  others: readonly { name: string; status: BatchStatus }[]
  /**
   * 이 배치의 Pre-flight 응답. `undefined` = 아직 못 받음(요청 전 · 도는 중 · 실패).
   * 🔴 「못 받음」을 「통과」로 접지 않는다.
   */
  preflight: PreflightResult | undefined
}

/** 시작을 막는 사유 문장들. 빈 배열 = 시작 가능(또는 시작이 뜻이 없는 상태) */
export function startBlockers(facts: StartFacts): string[] {
  if (facts.allowedCommands.includes('start')) return []
  if (!FROM_START.includes(facts.status)) return []
  // 스트림이 죽었으면 나머지 사실이 다 낡았다 — 그 한 줄만 말한다
  if (facts.streamStale) return [t('reason.streamStale')]

  const out: string[] = []
  if (!facts.canControl) out.push(t('startBlock.role'))
  if (!facts.lockHeld) out.push(t('startBlock.lock'))
  for (const other of facts.others) {
    if (START_BLOCKING_STATUSES.includes(other.status))
      out.push(
        t('startBlock.otherBatch', { name: other.name, status: batchStatusLabel(other.status) }),
      )
  }
  // ⚠ Pre-flight 는 쓰기 역할만 부를 수 있다(서버 `require_roles(WRITE_ROLES)`). 권한이
  //   없으면 부르지도 않으니 「못 받음」 줄도 세우지 않는다 — 역할 줄이 이미 말한다.
  if (facts.canControl) {
    if (facts.preflight === undefined) out.push(t('startBlock.preflightPending'))
    else if (!facts.preflight.passed) {
      // 🔴 막는 것은 `fail` 이다. `inconclusive` 는 서버 설정에 따라 통과한다(`startGate.ts`)
      const failing = facts.preflight.blockers.filter((b) => b.verdict === 'fail')
      const shown = failing.length > 0 ? failing : facts.preflight.blockers
      const codes = [...new Set(shown.map((b) => b.code))]
      out.push(t('startBlock.preflight', { codes: codes.join(', ') }))
    }
  }
  if (out.length === 0) out.push(t('startBlock.serverOnly'))
  return out
}

/** 툴팁 한 덩어리 — 머리말 + 한 줄에 하나 */
export function startBlockersText(lines: readonly string[]): string | undefined {
  if (lines.length === 0) return undefined
  return [t('startBlock.title'), ...lines.map((line) => `· ${line}`)].join('\n')
}
